/**
 * Подсказки связей.
 *
 * Система предлагает, но не связывает: она не знает замысла, а неверная
 * связь хуже отсутствующей — по ней потом строят выводы. Поэтому рядом с
 * каждым предложением написано, почему оно показано, и связь создаётся
 * одной явной кнопкой.
 *
 * Отклонённые подсказки запоминаются на устройстве: предлагать то, от чего
 * уже отказались, — самый быстрый способ научить человека не читать блок.
 */

import { CheckIcon, Loader2Icon, SparklesIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { RELATION_LABELS_RU } from '@project-os/contracts';
import { ApiError } from '@/api/client';
import { useRelationSuggestions } from '@/api/notifications';
import { useCreateRelation } from '@/api/collaboration';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocalSetting } from '@/lib/use-local-setting';
import { ItemTypeIcon } from './item-icon';

export function RelationSuggestions({
  projectId,
  itemId,
  canEdit,
  onOpenItem,
}: {
  projectId: string;
  itemId: string;
  canEdit: boolean;
  onOpenItem: (itemId: string) => void;
}) {
  const { data, isPending } = useRelationSuggestions(projectId, itemId, canEdit);
  const createRelation = useCreateRelation(projectId, itemId);

  const [dismissed, setDismissed] = useLocalSetting<string[]>(`dismissed-links-${itemId}`, []);
  const [pending, setPending] = useState<string | null>(null);

  if (!canEdit) return null;
  if (isPending) return <Skeleton className="h-16 w-full" />;

  const visible = (data ?? []).filter((entry) => !dismissed.includes(entry.item.id));
  if (visible.length === 0) return null;

  const link = async (toItemId: string, relationType: string, title: string) => {
    setPending(toItemId);
    try {
      await createRelation.mutateAsync({
        toItemId,
        relationType: relationType as Parameters<
          typeof createRelation.mutateAsync
        >[0]['relationType'],
      });
      toast.success(`Связано с «${title}»`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось создать связь');
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
        <SparklesIcon className="size-3" />
        Возможно, это связано с
      </p>

      <ul className="space-y-1">
        {visible.map((entry) => (
          <li
            key={entry.item.id}
            className="surface-card flex items-center gap-2 border border-border/50 p-2"
          >
            <ItemTypeIcon type={entry.item.type} className="size-3.5 shrink-0" />

            <button
              type="button"
              onClick={() => onOpenItem(entry.item.id)}
              className="min-w-0 flex-1 text-left"
            >
              <span className="block truncate text-sm hover:underline">{entry.item.title}</span>
              {/* Причина показывается человеку, а не только считается:
                  иначе подсказке нельзя ни доверять, ни возразить. */}
              <span className="block truncate text-2xs text-muted-foreground">
                {entry.reasonRu}
                {entry.workstreamName ? ` · ${entry.workstreamName}` : ''}
              </span>
            </button>

            <Button
              variant="outline"
              size="sm"
              className="h-7 shrink-0"
              disabled={pending !== null}
              onClick={() => void link(entry.item.id, entry.relationType, entry.item.title)}
            >
              {pending === entry.item.id ? <Loader2Icon className="animate-spin" /> : <CheckIcon />}
              {RELATION_LABELS_RU[entry.relationType].forward}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-muted-foreground"
              onClick={() => setDismissed([...dismissed, entry.item.id])}
              aria-label={`Скрыть подсказку «${entry.item.title}»`}
            >
              <XIcon className="size-3.5" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
